/**
 * @file CustomSelect.tsx
 * @module app/custom/Select/CustomSelect
 * @description Renders the CustomSelect reusable select control, including option presentation, creation, multi-selection, and table-filter behavior.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

/* eslint-disable jsx-a11y/anchor-is-valid */
import Select, { components } from 'react-select';
import { IMAGES } from '../../../utils/assetsAndPermissions';
import { useThemeMode } from '../../../_admin/partials';
const CaretDownIcon = () => {
  return (
    <img
      className="img-fluid pe-1"
      src={IMAGES.downArrow}
      alt=""
    />
  );
};
const DropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <CaretDownIcon />
    </components.DropdownIndicator>
  );
};
const customFilterOption = (option: any, rawInput: any) => {
  const optionData = option?.data || {};
  const optionTextSource =
    optionData.title ?? optionData.label ?? optionData.value ?? '';
  const input = String(rawInput ?? '').toLowerCase();
  const text = String(optionTextSource).toLowerCase();
  if (!input) {
    return true;
  }
  return text.includes(input);
};

export const normalizeSelectOptions = (options: any) => {
  return Array.isArray(options) ? options : [];
};

const CustomSelect = (props: any) => {
  const menuPortalZIndex = props.menuPortalZIndex ?? 9999;
  const { mode } = useThemeMode();
  const safeOptions = normalizeSelectOptions(props.options);
  const isDark = mode === 'dark';
  const darkBg = '#16213a';
  const darkBorder = '#2b3a52';
  const darkText = '#e2e8f0';
  return (
    <>
      <Select
        classNamePrefix="uw-select"
        onMenuClose={props.onMenuClose}
        onMenuOpen={props.onMenuOpen}
        openMenuOnClick={props.openMenuOnClick}
        isOptionDisabled={props.isOptionDisabled}
        components={{ DropdownIndicator }}
        hideSelectedOptions={props.hideSelectedOptions}
        value={props.value}
        menuPosition={'fixed'}
        isClearable={props.isClearable}
        menuPortalTarget={document.body}
        placeholder={props.placeholder}
        defaultValue={props.default}
        isLoading={props.isLoading}
        onChange={props.onChange}
        onInputChange={props.onInputChange}
        onMenuScrollToBottom={props.onMenuScrollToBottom}
        filterOption={customFilterOption}
        isDisabled={props.disabled}
        options={safeOptions}
        isSearchable={props.isSearchable}
        styles={{
          option: (base:any) => ({
            ...base,
            borderBottom: `1px solid ${isDark ? darkBorder : '#e0e0df'}`,
            ':last-child': {
              borderBottom: 'none',
            },
            margin: '0px',
            background: isDark ? darkBg : 'white',
            padding: '16px',
            ':active': {
              ...base[':active'],
              color: '#1b74e4',
              background: isDark ? '#1e293b' : '#f1faff',
            },
            ':hover': {
              ...base[':active'],
              color: '#1b74e4',
              background: isDark ? '#1e293b' : '#f1faff',
            },
            color: isDark ? darkText : '#1a1a1a',
            fontSize: props.optionFontSize ? props.optionFontSize : '1.154rem',
            fontWeight: '500',
          }),
          control: (base:any) => ({
            ...base,
            border: props.border
              ? '0.5px solid ' + props.border
              : `0.5px solid ${isDark ? darkBorder : '#e0e0df'}`,
            boxShadow: 'none',
            minHeight: props.minHeight ? props.minHeight : '40px',
            height: 'auto',
            minWidth: props.minWidth ? props.minWidth : '220px',
            width: 'auto',
            borderRadius: props.radiusTable ? props.radiusTable : '8px',
            padding: '0px',
            fontSize: props.controlFontSize
              ? props.controlFontSize
              : '1.231rem',
            fontWeight: '600',
            backgroundColor: props.disabled
              ? (isDark ? '#0f172a' : '#e5e5e5')
              : (isDark ? darkBg : props.backgroundColor),
            color: isDark ? darkText : undefined,
            pointerEvents: props.disabled ? 'none' : 'auto',
            ':hover': {
              ...base[':hover'],
              border: props.border
                ? '0.5px solid ' + props.border
                : `0.5px solid ${isDark ? darkBorder : '#e0e0df'}`,
            },
          }),
          menu: (base:any) => ({
            ...base,
            boxShadow: '0 10px 28px 0 rgba(0, 0, 0, 0.16)',
            backgroundColor: isDark ? darkBg : 'white',
            color: isDark ? darkText : '#1a1a1a',
          }),
          menuList: (base:any) => ({
            ...base,
            paddingTop: '0px',
            paddingBottom: '0px',
            borderRadius: '5px',
            width: 'auto',
            color: isDark ? darkText : '#1a1a1a',
          }),
          placeholder: (base: any) => ({
            ...base,
            color: isDark ? '#94a3b8' : "#a1a5b7",
          }),
          singleValue: (base: any) => ({
            ...base,
            color: isDark ? darkText : base.color,
          }),
          input: (base: any) => ({
            ...base,
            color: isDark ? darkText : base.color,
          }),
          menuPortal: (base:any, props:any) => ({
            ...base,
            zIndex: menuPortalZIndex,
          }),
          indicatorSeparator: (base:any) => ({
            ...base,
            display: 'none',
          }),
          indicatorsContainer: (base:any) => ({
            ...base,
            color: '#f9f9f9',
            strokeWidth: '5px',
          }),
          multiValueRemove: (base:any, { data }:any) => ({
            ...base,
            height: '12px',
            ':hover': {
              backgroundColor: '#e7f1fd',
            },
            svg: {
              height: '22px',
              width: '22px',
              fill: '#7c7c7c',
            },
          }),
          multiValue: (base:any) => ({
            ...base,
            backgroundColor: '#e7f1fd',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '11px 14px',
            borderRadius: '6px',
            fontSize: '1.231rem',
            fontWeight: '600',
            height: '42px',
            width: 'auto',
          }),
          multiValueLabel: (base:any) => ({
            fontSize: '1.231rem',
            fontWeight: '600',
          }),
        }}
        isMulti={props.isMulti}
      />
    </>
  );
};
export { CustomSelect };
